/**
 * theme/typography.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Two families, one scale, and a named role for every piece of text in the
 * product. Components consume the ROLES (`type.pageTitle`), never raw sizes —
 * that is what keeps twelve screens visually identical.
 *
 * The rule that matters: IBM Plex Mono carries every number, id, reference,
 * timestamp, eyebrow and currency code, always with tabular figures. Sans
 * carries prose. There is no third option.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Platform, type TextStyle } from 'react-native';
import { neutral } from './colors';

/**
 * Font family names. On web the Google-hosted faces are loaded by
 * web/index.html; on native the same TTFs are linked into the Android/iOS
 * projects under these exact PostScript names.
 */
export const family = {
  sans: Platform.select({
    web: '"IBM Plex Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    default: 'IBMPlexSans-Regular',
  }) as string,
  sansMedium: Platform.select({ web: undefined, default: 'IBMPlexSans-Medium' }),
  sansSemiBold: Platform.select({ web: undefined, default: 'IBMPlexSans-SemiBold' }),
  sansBold: Platform.select({ web: undefined, default: 'IBMPlexSans-Bold' }),
  mono: Platform.select({
    web: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    default: 'IBMPlexMono-Regular',
  }) as string,
  monoMedium: Platform.select({ web: undefined, default: 'IBMPlexMono-Medium' }),
  monoSemiBold: Platform.select({ web: undefined, default: 'IBMPlexMono-SemiBold' }),
} as const;

/**
 * React Native on Android ignores `fontWeight` unless the weighted face is
 * named explicitly, while web needs the weight and one family string. This
 * helper resolves both from one call site so no component has to know.
 */
function face(kind: 'sans' | 'mono', weight: '400' | '500' | '600' | '700'): TextStyle {
  if (Platform.OS === 'web') {
    return { fontFamily: kind === 'sans' ? family.sans : family.mono, fontWeight: weight };
  }
  const map: Record<string, string | undefined> = {
    'sans-400': family.sans,
    'sans-500': family.sansMedium,
    'sans-600': family.sansSemiBold,
    'sans-700': family.sansBold,
    'mono-400': family.mono,
    'mono-500': family.monoMedium,
    'mono-600': family.monoSemiBold,
  };
  return { fontFamily: map[`${kind}-${weight}`] ?? family.sans };
}

export const sans = (weight: '400' | '500' | '600' | '700' = '400') => face('sans', weight);
export const mono = (weight: '400' | '500' | '600' = '400') => face('mono', weight);

/** The size scale actually in use in the design — no value outside this list. */
export const size = {
  xxs: 9.5,
  eyebrow: 10,
  tableHeader: 10.5,
  metaSm: 11,
  meta: 11.5,
  label: 12,
  button: 12.5,
  body: 13,
  bodyLg: 13.5,
  sm: 14,
  md: 15,
  lg: 16,
  emptyTitle: 17,
  pageTitle: 20,
  kpi: 22,
  sectionTitle: 24,
  drawerBalance: 26,
  balance: 34,
} as const;

/** Body line-height ratio. 13px × 1.45 is the design's default leading. */
export const lineHeightRatio = 1.45;
export const leading = (px: number): number => Math.round(px * lineHeightRatio * 100) / 100;

/**
 * Tabular, LTR numerals. Money and ids must never reflow column-to-column as
 * digits change, and an amount must never be re-ordered by an RTL locale —
 * "AED 340.00" is a quantity, not a sentence.
 */
export const numeric: TextStyle = Platform.select({
  web: { fontVariant: ['tabular-nums'], writingDirection: 'ltr' } as TextStyle,
  default: { fontVariant: ['tabular-nums'], writingDirection: 'ltr' } as TextStyle,
});

/**
 * Named text roles. Every `<Text>` in the app spreads one of these; a screen
 * that needs a one-off variation overrides a single property on top of a role
 * rather than assembling a style from scratch.
 */
export const type = {
  /** h1 — 20 / 600 / -0.01em */
  pageTitle: { ...sans('600'), fontSize: size.pageTitle, letterSpacing: -0.2, color: neutral.ink },
  /** Onboarding h1 — 24 / 600 / -0.015em */
  onboardingTitle: {
    ...sans('600'),
    fontSize: size.sectionTitle,
    letterSpacing: -0.36,
    color: neutral.ink,
  },
  /** Page subtitle under an h1. */
  pageSubtitle: { ...sans('400'), fontSize: size.button, color: neutral.grey600 },
  cardTitle: { ...sans('600'), fontSize: size.body, color: neutral.ink },
  cardSubtitle: { ...sans('400'), fontSize: size.meta, color: neutral.grey600 },
  /** Table column header — 10.5 / uppercase / 0.06em. */
  tableHeader: {
    ...sans('500'),
    fontSize: size.tableHeader,
    letterSpacing: 0.63,
    textTransform: 'uppercase' as const,
    color: neutral.grey600,
  },
  /** Primary cell text. */
  cell: { ...sans('500'), fontSize: size.body, color: neutral.ink },
  /** Secondary cell text — the `type · ref` line under a holder name. */
  cellMeta: { ...mono('400'), fontSize: size.tableHeader, ...numeric, color: neutral.grey400 },
  /** KPI value — 22 mono 500. */
  kpiValue: { ...mono('500'), fontSize: size.kpi, ...numeric, color: neutral.ink },
  /** Custodian "you are holding" — 34 mono 500 / -0.01em. */
  balance: {
    ...mono('500'),
    fontSize: size.balance,
    letterSpacing: -0.34,
    ...numeric,
    color: neutral.ink,
  },
  /** Ledger drawer position — 26 mono 500. */
  drawerBalance: { ...mono('500'), fontSize: size.drawerBalance, ...numeric, color: neutral.ink },
  /** Eyebrow — 10 mono / uppercase / 0.12em / grey400. */
  eyebrow: {
    ...mono('500'),
    fontSize: size.eyebrow,
    letterSpacing: 1.2,
    textTransform: 'uppercase' as const,
    color: neutral.grey400,
  },
  /** Section label above a group of controls — same shape, darker. */
  eyebrowDark: {
    ...mono('500'),
    fontSize: size.metaSm,
    letterSpacing: 0.66,
    textTransform: 'uppercase' as const,
    color: neutral.ink2,
  },
  button: { ...sans('500'), fontSize: size.button, color: neutral.ink },
  body: {
    ...sans('400'),
    fontSize: size.body,
    lineHeight: leading(size.body),
    color: neutral.ink,
  },
  bodyMuted: {
    ...sans('400'),
    fontSize: size.body,
    lineHeight: leading(size.body),
    color: neutral.grey600,
  },
  meta: { ...sans('400'), fontSize: size.meta, color: neutral.grey600 },
  metaMono: { ...mono('400'), fontSize: size.meta, ...numeric, color: neutral.grey400 },
  /** Money, ids, references. */
  amount: { ...mono('500'), fontSize: size.body, ...numeric, color: neutral.ink },
  /** Empty-state / educational panel title. */
  emptyTitle: { ...sans('600'), fontSize: size.emptyTitle, color: neutral.ink },
  /** The wordmark in the header — 12.5 mono 600 uppercase 0.08em. */
  wordmark: {
    ...mono('600'),
    fontSize: size.button,
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
    color: neutral.ink,
  },
} as const;

export const typography = { family, size, type, sans, mono, numeric, leading } as const;
